import React, { MouseEventHandler } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { Variants } from 'framer-motion';

import { Contrast } from '@entur/layout';
import { useLocation } from '@reach/router';
import { UserIcon } from '@entur/icons';
import { IconButton } from '@entur/button';
import { colors } from '@entur/tokens';

import { LanguageContext } from '~/components/LanguageProvider';
import { Media } from '~/components/Layout';
import { LanguagePicker } from '../header/LanguagePicker';
import { MobileMenu } from './MobileMenu';
import Menu from './Menu';

import './Navigation.scss';
import './BurgerMenu.scss';

type NavigationProps = {
  isContrast?: boolean;
};

const NavigationStyles: Variants = {
  contrast: { background: 0 },
  standard: { background: 0 },
};
const Navigation: React.FC<NavigationProps> = ({ isContrast }) => {
  const location = useLocation();
  const { formatMessage: f } = useIntl();
  const [open, setOpen] = React.useState(false);
  const [anim, setAnim] = React.useState(false);

  const Element = isContrast || anim ? Contrast : 'div';
  const data = useStaticQuery(graphql`
    query {
      logoNegative: file(relativePath: { eq: "LogoNegative.svg" }) {
        publicURL
      }
      logoPositive: file(relativePath: { eq: "LogoPositive.svg" }) {
        publicURL
      }
    }
  `);

  React.useEffect(() => {
    if (open) {
      setAnim(true);
    }
  }, [open]);
  const MenuHasClosed = () => {
    setAnim(false);
  };

  const menuButtonClassNames = classNames('burger-menu-button', {
    'burger-menu-button--is-toggled': open,
  });

  const getIconClassNames = (index: number): string =>
    classNames(
      'burger-menu-button__icon',
      `burger-menu-button__icon__${index}`,
      {
        'burger-menu-button__icon--contrast': !isContrast && !open,
      }
    );

  const { language, setLanguage } = React.useContext(LanguageContext);
  const backdropRef = React.useRef<HTMLElement>(null);
  const headerRef = React.useRef<HTMLElement>(null);
  useOutsideAlerter(backdropRef);

  function useOutsideAlerter(ref: React.RefObject<HTMLElement>) {
    React.useEffect(() => {
      /**
       * Alert if clicked on outside of element
       */
      function handleClickOutside(event: any) {
        if (
          ref.current &&
          !ref.current.contains(event.target) &&
          !headerRef?.current?.contains(event.target)
        ) {
          setOpen(false);
        }
      }

      // Bind the event listener
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [ref]);
  }

  return (
    <header
      className={classNames('header-grid', { 'menu-is-open': open })}
      ref={headerRef}
    >
      <Media greaterThanOrEqual="desktop" className="omtur-desktop-navigation">
        <Element>
          <div
            style={{
              margin: `0 auto`,
              padding: `2rem 1.5rem`,
              maxWidth: '100rem',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <a
                href={'https://entur.no/'}
                style={{
                  marginRight: '2rem',
                  height: '2rem',
                  width: '6.625rem',
                }}
              >
                <img
                  height="32px"
                  alt="Tilbake til forsiden"
                  src={
                    isContrast || anim
                      ? data.logoNegative.publicURL
                      : data.logoPositive.publicURL
                  }
                />
              </a>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div className="omtur-desktop-navigation__language-picker">
                <LanguagePicker
                  iconClassName="omtur-desktop-navigation__language-picker__icon"
                  labelClassName="omtur-desktop-navigation__language-picker__label"
                />
              </div>
              <IconButton
                style={{
                  width: 'unset',
                  fontFamily: 'unset',
                  textDecoration: 'none',
                  marginLeft: '1rem',
                }}
                as="a"
                href="https://www.entur.no/min-side"
              >
                <span
                  style={{
                    fontWeight: 600,
                    marginRight: '0.5rem',
                    fontSize: '1rem',
                  }}
                >
                  <FormattedMessage id="navigation.minSide" />
                </span>
                <UserIcon size="20" />
              </IconButton>
              <IconButton
                style={{
                  fontFamily: 'inherit',
                  width: 'unset',
                  height: 'unset',
                  marginLeft: '1rem',
                }}
                className={menuButtonClassNames}
                onClick={() => setOpen(!open)}
                aria-label={f({ id: 'navigation.menu' })}
              >
                <span
                  style={{
                    marginRight: '0.5rem',
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                >
                  <FormattedMessage id="navigation.menu" />
                </span>
                <span aria-hidden="true" className="burger-menu-button__icons">
                  <span className={getIconClassNames(1)} />
                  <span className={getIconClassNames(2)} />
                  <span className={getIconClassNames(3)} />
                </span>
              </IconButton>
            </div>
          </div>
        </Element>
        <Menu
          open={open}
          closeMenu={() => setOpen(false)}
          isDoneAnimate={() => MenuHasClosed()}
          ref={backdropRef}
        />
      </Media>
      <Media at="mobile" className="omtur-mobile-navigation">
        <Element style={{ display: 'flex', flexDirection: 'column' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              padding: '0.75rem 1.5rem 0.5rem',
              background:
                isContrast || anim ? colors.blues.blue10 : colors.greys.grey90,
            }}
          >
            <div className="omtur-mobile-navigation__language-picker">
              <LanguagePicker
                iconClassName="omtur-mobile-navigation__language-picker__icon"
                labelClassName="omtur-mobile-navigation__language-picker__label"
              />
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0.5rem 1.5rem',
            }}
          >
            <div>
              <a href="https://entur.no/">
                <img
                  height="24px"
                  style={{ margin: '1rem 0' }}
                  src={
                    isContrast || anim
                      ? data.logoNegative.publicURL
                      : data.logoPositive.publicURL
                  }
                />
              </a>
            </div>
            <div style={{ display: 'flex' }}>
              <IconButton
                style={{
                  width: 'unset',
                  height: 'unset',
                  fontFamily: 'unset',
                  textDecoration: 'none',
                  alignItems: 'center',
                }}
                as="a"
                href="https://www.entur.no/min-side"
              >
                <span
                  style={{
                    fontWeight: 600,
                    marginRight: '0.5rem',
                    fontSize: '1rem',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <FormattedMessage id="navigation.minSide" />
                </span>
                <UserIcon size="20" />
              </IconButton>
              <IconButton
                style={{
                  fontFamily: 'inherit',
                  width: 'unset',
                  height: 'unset',
                  marginLeft: '1rem',
                  alignItems: 'center',
                }}
                onClick={() => setOpen(!open)}
                aria-label={f({ id: 'navigation.menu' })}
              >
                <span style={{ marginRight: '0.5rem', fontWeight: 600 }}>
                  <FormattedMessage id="navigation.menu" />
                </span>
                <div aria-hidden="true" className="burger-menu-button__icons">
                  <span className={getIconClassNames(1)} />
                  <span className={getIconClassNames(2)} />
                  <span className={getIconClassNames(3)} />
                </div>
              </IconButton>
            </div>
          </div>
        </Element>
        <MobileMenu
          open={open}
          isDoneAnimate={() => MenuHasClosed()}
          closeMenu={() => setOpen(false)}
        />
      </Media>
    </header>
  );
};

export default Navigation;
